/**
 * The turn, spoken (the accessibility brief's first checkbox) — one polite live region, and the only one the story
 * screen keeps open for prose.
 *
 * A sighted player watches fifty seconds of a turn resolve: the ticker fills, the phase line
 * changes, the narration writes itself. A screen-reader user gets whatever the live regions
 * say, and *what* they say is a design decision with two ways to get it wrong. Announce too
 * little and the wait is fifty seconds of silence after pressing a button. Announce too much —
 * point a live region at the streaming paragraph itself — and it is fifty seconds of
 * half-words, because every `narration.delta` is a text change.
 *
 * So the spoken track is assembled here rather than emerging from whichever elements happen to
 * be live:
 *
 * - **The DM's phase label**, which changes a handful of times per turn, and the stall notice.
 * - **Narration, one completed sentence at a time** (`announce.ts`), which is the accessibility brief's *"announce
 * on sentence or on completion"* taken literally.
 *
 * **The visible prose is deliberately not a live region.** It stays an ordinary `<article>`
 * that a reader can navigate, re-read and skim with their own cursor — which is the thing a
 * live region cannot give them. This region is a *transcript* running alongside it, `sr-only`,
 * and the duplication is the point: one copy to hear as it happens, one to read at leisure.
 *
 * **It is mounted unconditionally, and that is what makes the last sentence survive.** The
 * turn store is cleared the instant the chapter is committed to the cache, so a component that
 * unmounted with the live scene would take the region down in the same frame it needed to
 * speak the tail. `narration → ''` is read as *the turn ended* rather than as *nothing to say*
 * (`continuesNarration`), which is the one piece of state this component keeps of its own.
 */
import { useEffect, useRef, useState } from 'react';
import { announceableSentences, continuesNarration } from '@/features/turn/announce';
import { useT } from '@/i18n/context';

interface TurnAnnouncerProps {
  /** `turn.narration` — everything the deltas have appended, or `''` between turns. */
  narration: string;
  /** No stream is open: the tail is as complete as it is going to get. */
  complete: boolean;
  /**
   * What the turn is doing, in words — the DM's own `progress.label`, or the stall notice.
   * Null when nothing is running, which is most of the time.
   */
  status: string | null;
}

export function TurnAnnouncer({ narration, complete, status }: TurnAnnouncerProps) {
  const t = useT();
  const spoken = useSpokenNarration(narration, complete);

  return (
    <div
      // Polite, never assertive: this must queue behind whatever the reader is doing, not
      // interrupt it. `aria-atomic="false"` so an added sentence is announced on its own
      // rather than dragging the whole transcript with it — the default for a live region is
      // atomic in some readers, and the difference is one sentence versus four paragraphs.
      aria-live="polite"
      aria-atomic="false"
      aria-label={t('ui.a11y.turn_updates')}
      className="sr-only"
    >
      {status === null ? null : <p>{status}</p>}
      {spoken.map((sentence, index) => (
        // Position is the identity: the list is append-only within a turn, which is the
        // property that makes an added node mean "this is new, say it".
        <p key={index}>{sentence}</p>
      ))}
    </div>
  );
}

/**
 * The sentences said so far, growing as the stream does.
 *
 * The only subtlety is what counts as a *new* turn. Narration is append-only while one runs,
 * so anything that is not a continuation is either the commit clearing the store (empty, and
 * the cue to flush the tail) or the next turn's first delta (different prose, and the cue to
 * start over). Both fall out of one prefix test.
 */
function useSpokenNarration(narration: string, complete: boolean): readonly string[] {
  const [spoken, setSpoken] = useState<readonly string[]>([]);
  // The high-water mark of the current turn's prose. Kept because `narration` itself goes
  // back to `''` at the commit, and the tail still has to be spoken from somewhere.
  const seen = useRef('');

  useEffect(() => {
    const restarted = narration !== '' && !continuesNarration(seen.current, narration);
    if (narration !== '') seen.current = narration;

    // No stream, or no store: either way there is nothing more coming for this text.
    const ended = complete || narration === '';
    const chunks = announceableSentences(seen.current, ended);

    setSpoken((current) => {
      if (restarted) return chunks;
      // Never shrink. A re-render that recomputes the same list must add no nodes, or the
      // region announces sentences the reader has already heard.
      return chunks.length > current.length ? chunks : current;
    });
  }, [narration, complete]);

  return spoken;
}
